import { SkeletonLoader, TooltipTrigger } from '@toggle/design-system';
import React from 'react';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { CatalystInsightIcon } from '~/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';

import { CompanyCatalystCounterTooltip } from '../company-catalyst-counter-tooltip/CompanyCatalystCounterTooltip';
import { useInsightsCellData } from '../hooks/useInsightsCellData';
import styles from './InsightsKGCell.module.scss';

const MAX_KG_INSIGHTS = 2;

export type InsightsKGCellProps = {
  entityTag: string;
  isDisabled: boolean;
  source: PosthogTrackingSource;
};

export const InsightsKGCell = ({
  entityTag,
  isDisabled,
  source,
}: InsightsKGCellProps) => {
  const {
    isLoading,
    filteredCatalystInsights = [],
    earningsInsight,
  } = useInsightsCellData({ entityTag });

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              height: '20px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  const visibleCatalystInsights =
    filteredCatalystInsights.length >= MAX_KG_INSIGHTS
      ? filteredCatalystInsights.slice(0, MAX_KG_INSIGHTS - 1)
      : filteredCatalystInsights.slice(0, MAX_KG_INSIGHTS);

  const remainingCatalystInsights =
    filteredCatalystInsights.length >= MAX_KG_INSIGHTS
      ? filteredCatalystInsights.slice(MAX_KG_INSIGHTS - 1)
      : filteredCatalystInsights.slice(MAX_KG_INSIGHTS);

  return (
    <div className={styles.insightsContainer}>
      {earningsInsight ? (
        <EarningsInsightIcon
          entityTag={entityTag}
          earningsInsight={earningsInsight}
          width={18}
          height={20}
          source={source}
          isDisabled={isDisabled}
        />
      ) : (
        <div className={styles.placeholder} />
      )}
      {visibleCatalystInsights.map(catalystInsight => (
        <CatalystInsightIcon
          key={catalystInsight.id}
          catalystInsightId={catalystInsight.id}
          subCategory={catalystInsight.sub_category}
          isBordered
          customSize={{ width: 18, height: 20 }}
          identifier={entityTag}
          isDisabled={isDisabled}
          source={source}
          category={catalystInsight.category}
        />
      ))}
      {!!remainingCatalystInsights.length && (
        <TooltipContainer
          label={
            <CompanyCatalystCounterTooltip
              insights={remainingCatalystInsights}
              source={source}
            />
          }
          inPortal
          trigger={[TooltipTrigger.Hover]}
          placement="top-end"
          withSafePolygon
          disabled={isDisabled}
        >
          <div className={isDisabled ? styles.countDisabled : styles.count}>
            +{remainingCatalystInsights.length}
          </div>
        </TooltipContainer>
      )}
    </div>
  );
};
